<script setup>
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
import CodemirrorModeMenu from '../../common/codemirror/CodemirrorModeMenu';
import CodemirrorLineNumbersBtn from '../../common/codemirror/CodemirrorLineNumbersBtn';
import CodemirrorLineWrappingBtn from '../../common/codemirror/CodemirrorLineWrappingBtn';
import CodemirrorTwigModeBtn from '../../common/codemirror/CodemirrorTwigModeBtn';

const props = defineProps({
    options: { type: Object, required: true }
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---
const codeMirrorTextArea = ref(null);
const cmInstance = ref(null);

const formId = props.options.formId;
const currentData = props.options.currentData;
const field = props.options.field;
const tableName = props.options.tableName;
const mode = props.options.mode;
const value = props.options.value;
const readOnly = !!props.options.readOnly;
const onSaveToField = props.options.onSaveToField;
const state = props.options.state;

const currentValue = ref(currentData[field.name] || '');
const originalValue = ref(props.options.originalValue || '');
const idExists = ref(!!currentData.id);
const modalTitle = ref(props.options.modalTitle || '&nbsp;');
const showTwigControl = ref(mode === 'twig' || mode === 'text/html');

// --- Computed ---
const changesExist = computed(() => originalValue.value !== currentValue.value);

const theme = computed(() => store.getters['user/pref']('codeMirror_theme'));
const lineNumbers = computed(() => store.getters['user/pref']('codeMirror_lineNumbers'));
const lineWrapping = computed(() => store.getters['user/pref']('codeMirror_lineWrapping'));
const twigMode = computed(() => store.getters['user/pref']('codeMirror_twigMode'));

// --- Watchers ---
watch(theme, (val) => cmInstance.value?.setOption('theme', val));
watch(lineNumbers, (val) => cmInstance.value?.setOption('lineNumbers', val));
watch(lineWrapping, (val) => cmInstance.value?.setOption('lineWrapping', val));
watch(twigMode, (val) => {
    let m = mode;
    if (m === 'text/html' || m === 'twig') {
        m = val ? 'twig' : 'text/html';
    }
    cmInstance.value?.setOption('mode', m);
});

// --- Methods ---
const init = () => {
    let options = {
        value: value || '',
        mode: mode,
        lineNumbers: true,
        lineWrapping: false,
        readOnly: readOnly,
        indentUnit: 4,
        tabMode: 'spaces',
        enterMode: 'indent',
        electricChars: false,
        autofocus: true,
        autoCloseTags: true
    };

    if (theme.value) options.theme = theme.value;
    if (lineNumbers.value !== undefined) options.lineNumbers = lineNumbers.value;
    if (lineWrapping.value !== undefined) options.lineWrapping = lineWrapping.value;

    if (twigMode.value !== undefined && (mode === 'text/html' || mode === 'twig')) {
        options.mode = twigMode.value ? 'twig' : 'text/html';
    }

    if (mode === 'javascript' || mode === 'css') {
        options.gutters = ['CodeMirror-lint-markers'];
        options.lint = true;
    }

    nextTick(() => {
        // eslint-disable-next-line no-undef
        cmInstance.value = CodeMirror.fromTextArea(codeMirrorTextArea.value, options);
        cmInstance.value.setSize('100%', '100%');

        if (state) {
            if (state.history) cmInstance.value.setHistory(state.history);
            cmInstance.value.on('change', () => {
                state.history = cmInstance.value.getHistory();
            });
        }

        cmInstance.value.setOption('extraKeys', {
            Tab: (cm) => {
                let spaces = Array(cm.getOption('indentUnit') + 1).join(' ');
                cm.replaceSelection(spaces);
            }
        });

        cmInstance.value.on('change', () => {
            currentValue.value = cmInstance.value.getValue();
        });

        // FIX: Ensure content appears after modal transition
        setTimeout(() => {
            cmInstance.value.refresh();
        }, 250);
    });
};

const saveToField = () => {
    originalValue.value = currentValue.value;
    if (field.name) {
        currentData[field.name] = currentValue.value;
    }
    if (onSaveToField) {
        onSaveToField.call(proxy, { newValue: currentValue.value });
    }
};

const saveToDatabase = () => {
    if (field.name) {
        store.dispatch('request/patch', {
            url: `api/component/${tableName}/${currentData.id}`,
            postData: { [field.name]: currentValue.value }
        }).then(() => {
            store.commit('cacheNeedsClearing');
        });

        store.commit(`${formId}/presetData`, {
            name: field.name,
            value: currentValue.value,
        });
    }
};

const discardChanges = () => {
    if (confirm('Are you sure? You have unsaved changes')) {
        emit('hide-modal');
    }
};

const saveChanges = () => {
    saveToField();
    saveToDatabase();
};

const saveChangesAndCloseModal = () => {
    saveToField();
    emit('hide-modal');
};

// --- Lifecycle ---
onMounted(() => {
    init();
});
</script>

<template>
    <div class="modal-dialog modal-fullscreen code-mirror-modal">
        <div class="modal-content">
            <div class="modal-header d-flex py-1">
                <h4 class="modal-title flex-fill fs-6" v-html="modalTitle" />

                <div class="d-flex align-items-center">
                    <div class="dropdown">
                        <button class="btn btn-sm btn-light" data-bs-toggle="dropdown" type="button">
                            <i class="bi-three-dots" />
                        </button>

                        <ul class="dropdown-menu dropdown-menu-end" @click.stop>
                            <li class="dropdown-item text-center">
                                <CodemirrorModeMenu />
                            </li>
                            <CodemirrorLineNumbersBtn />
                            <CodemirrorLineWrappingBtn />
                            <CodemirrorTwigModeBtn v-if="showTwigControl" />
                        </ul>
                    </div>

                    <button aria-label="Close" class="btn-close ms-2" type="button" @click="$emit('hide-modal')"></button>
                </div>
            </div>

            <div class="modal-body p-0 h-100">
                <textarea ref="codeMirrorTextArea">{{ value }}</textarea>
            </div>

            <div class="modal-footer py-1">
                <button
                    v-if="!readOnly"
                    :class="{ 'btn-light': !changesExist, 'btn-warning': changesExist }"
                    :disabled="!changesExist || !idExists"
                    class="btn btn-sm d-flex align-items-center"
                    type="button"
                    @click="saveChanges"
                >
                    Save
                </button>

                <button
                    v-if="!readOnly"
                    :class="{ 'btn-light': !changesExist, 'btn-warning': changesExist }"
                    class="btn btn-sm"
                    type="button"
                    @click="saveChangesAndCloseModal"
                >
                    Close
                </button>

                <button
                    v-if="changesExist"
                    class="btn btn-light btn-sm modal-discard-changes"
                    type="button"
                    @click="discardChanges"
                >
                    Discard changes
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Ensure CodeMirror occupies full height of modal body */
:deep(.CodeMirror) {
    height: 100% !important;
}
</style>